import type { RouteRecordRaw } from 'vue-router'
import HomePage from '@/pages/HomePage.vue'
import {
    getPreferredLanguage,
    SUPPORTED_LANGUAGES,
    DEFAULT_LANGUAGE,
    type Language,
} from '@/composables/useTranslations'

// Re-export for convenience (aliased to "locale" terminology for routes)
export {
    getPreferredLanguage as getPreferredLocale,
    SUPPORTED_LANGUAGES as SUPPORTED_LOCALES,
    DEFAULT_LANGUAGE as DEFAULT_LOCALE,
}
export type { Language as SupportedLocale }

/**
 * Creates a redirect function that redirects to the localized version of a path.
 */
function createLocaleRedirect(path: string) {
    return () => {
        const locale = getPreferredLanguage()
        return `/${locale}${path}`
    }
}

// Page paths that need locale redirects (without leading slash)
const PAGE_PATHS = ['about', 'pricing', 'roadmap', 'use-cases', 'blog', 'imprint', 'privacy']

// Routes array for vite-ssg
export const routes: RouteRecordRaw[] = [
    {
        path: '/:lang(en|de)',
        name: 'Home',
        component: HomePage,
    },
    {
        path: '/:lang(en|de)/about',
        name: 'About',
        component: () => import('@/pages/AboutPage.vue'),
    },
    {
        path: '/:lang(en|de)/pricing',
        name: 'Pricing',
        component: () => import('@/pages/PricingPage.vue'),
    },
    {
        path: '/:lang(en|de)/roadmap',
        name: 'Roadmap',
        component: () => import('@/pages/RoadmapPage.vue'),
    },
    {
        path: '/:lang(en|de)/use-cases',
        name: 'UseCases',
        component: () => import('@/pages/UseCasesPage.vue'),
    },
    {
        path: '/:lang(en|de)/blog',
        name: 'Blog',
        component: () => import('@/pages/BlogPage.vue'),
    },
    {
        path: '/:lang(en|de)/blog/:slug',
        name: 'BlogPost',
        component: () => import('@/pages/BlogPostPage.vue'),
    },
    {
        path: '/:lang(en|de)/imprint',
        name: 'Imprint',
        component: () => import('@/pages/ImprintPage.vue'),
        meta: { noIndex: true },
    },
    {
        path: '/:lang(en|de)/privacy',
        name: 'Privacy',
        component: () => import('@/pages/PrivacyPage.vue'),
        meta: { noIndex: true },
    },
    // Root path redirect with locale detection
    {
        path: '/',
        redirect: createLocaleRedirect(''),
        meta: { noIndex: true },
    },
    // Redirects for pages without locale prefix
    ...PAGE_PATHS.map(pagePath => ({
        path: `/${pagePath}`,
        redirect: createLocaleRedirect(`/${pagePath}`),
        meta: { noIndex: true },
    })),
    // Catch-all redirect for any other unmatched paths
    {
        path: '/:pathMatch(.*)*',
        redirect: createLocaleRedirect(''),
    },
]

// Export default for backward compatibility with tests
export default { routes }
